package com.partejar.app.core.session

import com.partejar.app.feature.auth.data.repository.AuthRepository
import com.partejar.app.feature.workspace.data.SessionBootstrapDto
import com.partejar.app.feature.workspace.data.SessionUser
import com.partejar.app.feature.workspace.data.UserWorkspace
import com.partejar.app.feature.workspace.data.WorkspaceRepository
import com.partejar.app.feature.entitlements.presentation.EntitlementCoordinator
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

enum class SessionDestination { HOME, WORKSPACE_SELECTION, FIRST_WORKSPACE }

interface SessionIdentityStore {
    fun updateIdentity(user: SessionUser)
}

class SessionCoordinator(
    private val authRepository: AuthRepository,
    private val workspaceRepository: WorkspaceRepository,
    private val currentWorkspaceRepository: CurrentWorkspaceRepository,
    private val entitlementCoordinator: EntitlementCoordinator? = null
) : SessionIdentityStore {
    private val _bootstrap = MutableStateFlow<SessionBootstrapDto?>(null)
    val bootstrap: StateFlow<SessionBootstrapDto?> = _bootstrap.asStateFlow()

    suspend fun resolve(): SessionDestination {
        val session = workspaceRepository.bootstrap()
        _bootstrap.value = session
        val savedId = currentWorkspaceRepository.currentWorkspaceId()
        val savedWorkspace = session.workspaces.firstOrNull { it.id == savedId }
        val destination = when {
            session.workspaces.isEmpty() -> {
                currentWorkspaceRepository.clear()
                SessionDestination.FIRST_WORKSPACE
            }
            session.workspaces.size == 1 -> {
                currentWorkspaceRepository.selectWorkspace(session.workspaces.single().id)
                SessionDestination.HOME
            }
            savedWorkspace != null -> SessionDestination.HOME
            else -> {
                currentWorkspaceRepository.clear()
                SessionDestination.WORKSPACE_SELECTION
            }
        }
        entitlementCoordinator?.onSessionReady()
        return destination
    }

    suspend fun resolveAndSelect(workspaceId: Long): SessionDestination {
        val session = workspaceRepository.bootstrap()
        _bootstrap.value = session
        require(session.workspaces.any { it.id == workspaceId }) {
            "O workspace do convite não está disponível para esta sessão."
        }
        currentWorkspaceRepository.selectWorkspace(workspaceId)
        entitlementCoordinator?.onSessionReady()
        return SessionDestination.HOME
    }

    fun select(workspaceId: Long) {
        require(_bootstrap.value?.workspaces?.any { it.id == workspaceId } == true) {
            "Workspace indisponível para a sessão atual."
        }
        currentWorkspaceRepository.selectWorkspace(workspaceId)
        entitlementCoordinator?.onWorkspaceChanged()
    }

    fun currentWorkspace(): UserWorkspace? {
        val currentId = currentWorkspaceRepository.currentWorkspaceId()
        return _bootstrap.value?.workspaces?.firstOrNull { it.id == currentId }
    }

    fun upsert(workspace: UserWorkspace, select: Boolean) {
        val session = _bootstrap.value ?: return
        val updated = session.workspaces.filterNot { it.id == workspace.id } + workspace
        _bootstrap.value = session.copy(workspaces = updated.sortedBy { it.nome.lowercase() })
        if (select) {
            currentWorkspaceRepository.selectWorkspace(workspace.id)
            entitlementCoordinator?.onSessionReady()
        } else {
            entitlementCoordinator?.refreshPersonal()
        }
    }

    override fun updateIdentity(user: SessionUser) {
        val session = _bootstrap.value ?: return
        _bootstrap.value = session.copy(usuario = user)
    }

    suspend fun logout() {
        authRepository.logout()
        _bootstrap.value = null
        entitlementCoordinator?.clear()
    }

    fun clearLocalSession() {
        authRepository.clearLocalSession()
        _bootstrap.value = null
        entitlementCoordinator?.clear()
    }
}
